import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Segmented, Flex, Spin, Select } from 'antd';
import { LoadingOutlined } from '@ant-design/icons';

import ReactEChartsCore from 'echarts-for-react/lib/core';
import { BarChart, LineChart } from 'echarts/charts';
import * as echarts from 'echarts/core';
import {
  GridComponent,
  LegendComponent,
  TitleComponent,
  TooltipComponent
} from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';

import { getColor, numberFormatter, formatCurrency } from '../../../helpers/utils';
import SnapshotService from '../services';
import CardEmptyResult from 'components/common/pageComponents/CardEmptyResult';
import { PiPackageDuotone } from 'react-icons/pi';

echarts.use([
  TitleComponent,
  TooltipComponent,
  GridComponent,
  BarChart,
  LineChart,
  CanvasRenderer,
  LegendComponent
]);

const InventoryItemChart = ({ 
  inventoryItemId, 
  productId, 
  supplierId, 
  categoryId, 
  period = 'current_month',
  chartType = 'bar' // 'bar' or 'line'
}) => {
  const [currency_symbol, setCurrencySymbol] = useState('$');
  const [data, setData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [currentPeriod, setCurrentPeriod] = useState(period);
  const [currentChartType, setCurrentChartType] = useState(chartType);

  const getData = useCallback(() => {
    setIsLoading(true);
    const params = {
      period: currentPeriod,
      sort_by: currentPeriod === 'current_month' ? 'current_month_in_quantity' :
               currentPeriod === 'last_30_days' ? 'last_30_days_in_quantity' :
               currentPeriod === 'last_90_days' ? 'last_90_days_in_quantity' :
               currentPeriod === 'current_year' ? 'current_year_in_quantity' : 'total_in_quantity',
      sort_order: 'desc',
      limit: 10
    };
    
    if (inventoryItemId) params.inventory_item_id = inventoryItemId;
    if (productId) params.item_id = productId;
    if (supplierId) params.supplier_id = supplierId;
    if (categoryId) params.category_id = categoryId;

    SnapshotService.getInventoryItemSnapshots(params).then((response) => {
      try {
        if (response.data?.success && response.data?.data) {
          const { data: chartData, currency } = response.data.data;
          setData(chartData || []);
          setCurrencySymbol(currency?.currency_symbol || '$');
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Error fetching inventory item snapshot data:', error);
        setIsLoading(false);
      }
    }).catch(error => {
      console.error('Error fetching inventory item snapshot data:', error);
      setIsLoading(false);
    });
  }, [currentPeriod, inventoryItemId, productId, supplierId, categoryId]);

  useEffect(() => {
    getData();
  }, [getData]);

  const changePeriod = useCallback((value) => {
    setCurrentPeriod(value);
  }, []);

  const changeChartType = useCallback((value) => {
    setCurrentChartType(value);
  }, []);

  const itemNames = useMemo(() => data.map(item => item.name || item.sku || 'N/A'), [data]);
  
  const getInData = useMemo(() => {
    return data.map(item => {
      switch (currentPeriod) {
        case 'current_month':
          return parseFloat(item.current_month_in_quantity) || 0;
        case 'last_30_days':
          return parseFloat(item.last_30_days_in_quantity) || 0;
        case 'last_90_days':
          return parseFloat(item.last_90_days_in_quantity) || 0;
        case 'current_year':
          return parseFloat(item.current_year_in_quantity) || 0;
        default:
          return parseFloat(item.total_in_quantity) || 0;
      }
    });
  }, [data, currentPeriod]);

  const getOutData = useMemo(() => {
    return data.map(item => {
      switch (currentPeriod) {
        case 'current_month':
          return parseFloat(item.current_month_out_quantity) || 0;
        case 'last_30_days':
          return parseFloat(item.last_30_days_out_quantity) || 0;
        case 'last_90_days':
          return parseFloat(item.last_90_days_out_quantity) || 0;
        case 'current_year':
          return parseFloat(item.current_year_out_quantity) || 0;
        default:
          return parseFloat(item.total_out_quantity) || 0;
      }
    });
  }, [data, currentPeriod]);

  const getStockData = useMemo(() => {
    return data.map(item => parseFloat(item.current_stock) || 0);
  }, [data]);

  const tooltipFormatter = useCallback((params) => {
    if (!Array.isArray(params) || params.length === 0) {
      return '';
    }

    const firstParam = params[0];
    const itemName = firstParam.name || '';
    const index = firstParam.dataIndex;
    const item = data[index];
    
    if (!item) return '';

    let inQty = 0, outQty = 0, stock = 0;
    params.forEach(param => {
      const value = typeof param.value === 'object' ? param.value[1] : param.value;
      const numValue = parseFloat(value) || 0;
      
      if (param.seriesName === 'In') {
        inQty = numValue;
      } else if (param.seriesName === 'Out') {
        outQty = numValue;
      } else if (param.seriesName === 'Stock') {
        stock = numValue;
      }
    });

    return `
      <div style="padding: 8px;">
        <div style="font-weight: 600; margin-bottom: 4px;">${item.name || itemName}</div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('success')}; margin-right: 6px;"></span>
          In: ${numberFormatter(inQty, 2)} ${item.unit_of_measurement || ''}
        </div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('danger')}; margin-right: 6px;"></span>
          Out: ${numberFormatter(outQty, 2)} ${item.unit_of_measurement || ''}
        </div>
        <div style="margin: 4px 0;">
          <span style="display: inline-block; width: 10px; height: 10px; background: ${getColor('info')}; margin-right: 6px;"></span>
          Current Stock: ${numberFormatter(stock, 2)} ${item.unit_of_measurement || ''}
        </div>
        ${item.is_low_stock ? '<div style="margin: 4px 0; color: #ff4d4f;">⚠ Low Stock</div>' : ''}
      </div>
    `;
  }, [data]);

  const getOptions = useMemo(() => {
    const series = [];
    
    if (currentChartType === 'bar') {
      series.push(
        {
          name: 'In',
          type: 'bar',
          data: getInData,
          itemStyle: {
            color: getColor('success')
          },
          stack: 'inventory',
          barMaxWidth: 50
        },
        {
          name: 'Out',
          type: 'bar',
          data: getOutData.map(qty => -qty), // Negative for display below zero
          itemStyle: {
            color: getColor('danger')
          },
          stack: 'inventory',
          barMaxWidth: 50
        },
        {
          name: 'Stock',
          type: 'line',
          data: getStockData,
          lineStyle: {
            color: getColor('info'),
            width: 2
          },
          itemStyle: {
            color: getColor('info')
          },
          symbol: 'circle',
          symbolSize: 6,
          yAxisIndex: 1
        }
      );
    } else {
      series.push(
        {
          name: 'In',
          type: 'line',
          data: getInData,
          lineStyle: {
            color: getColor('success'),
            width: 2
          },
          itemStyle: {
            color: getColor('success')
          },
          symbol: 'circle',
          symbolSize: 6
        },
        {
          name: 'Out',
          type: 'line',
          data: getOutData,
          lineStyle: {
            color: getColor('danger'),
            width: 2
          },
          itemStyle: {
            color: getColor('danger')
          },
          symbol: 'circle',
          symbolSize: 6
        },
        {
          name: 'Stock',
          type: 'line',
          data: getStockData,
          lineStyle: {
            color: getColor('info'),
            width: 2
          },
          itemStyle: {
            color: getColor('info')
          },
          symbol: 'circle',
          symbolSize: 6,
          yAxisIndex: 1
        }
      );
    }

    return {
      tooltip: {
        trigger: 'axis',
        formatter: tooltipFormatter,
        backgroundColor: 'rgba(0, 0, 0, 0.8)',
        borderColor: 'transparent',
        textStyle: { color: '#fff' }
      },
      legend: {
        data: ['In', 'Out', 'Stock'],
        bottom: 0,
        textStyle: {
          color: getColor('gray-400')
        }
      },
      xAxis: {
        type: 'category',
        data: itemNames,
        axisPointer: { type: 'shadow' },
        axisLabel: {
          color: getColor('gray-400'),
          rotate: itemNames.length > 5 ? 45 : 0,
          interval: 0
        },
        axisTick: { show: false },
        axisLine: { show: false }
      },
      yAxis: [
        {
          type: 'value',
          name: 'Quantity',
          axisPointer: { show: false },
          splitLine: {
            lineStyle: {
              color: getColor('gray-300'),
              type: 'dashed'
            }
          },
          axisLabel: {
            show: true,
            color: getColor('gray-400'),
            formatter: (value) => {
              return numberFormatter(Math.abs(value), 2);
            },
            margin: 15
          },
          axisTick: { show: false },
          axisLine: { show: false }
        },
        {
          type: 'value',
          name: 'Stock',
          axisPointer: { show: false },
          splitLine: { show: false },
          axisLabel: {
            show: true,
            color: getColor('gray-400'),
            formatter: (value) => {
              return numberFormatter(value, 2);
            },
            margin: 15
          },
          axisTick: { show: false },
          axisLine: { show: false }
        }
      ],
      series: series,
      grid: { right: 10, left: 0, bottom: 50, top: 10, containLabel: true }
    };
  }, [itemNames, tooltipFormatter, getInData, getOutData, getStockData, currentChartType]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '300px' }}>
        <Spin indicator={<LoadingOutlined spin />} />
      </div>
    );
  }

  const hasData =
    data.length > 0 &&
    (getInData.some(value => value > 0) ||
      getOutData.some(value => value > 0) ||
      getStockData.some(value => value > 0));

  if (!hasData) {
    return (
      <CardEmptyResult
        title="No inventory analytics data"
        subtitle="There is no inventory movement or stock data available for the selected criteria."
        icon={<PiPackageDuotone className="fs-2 text-secondary" />}
      />
    );
  }

  return (
    <>
      <Flex justify="space-between" align="center" style={{ marginBottom: '16px' }}>
        <h6>Inventory Items</h6>
        <Flex gap="small">
          <Select
            size="small"
            value={currentChartType}
            onChange={changeChartType}
            options={[
              { label: 'Bar', value: 'bar' },
              { label: 'Line', value: 'line' },
            ]}
          />
          <Segmented
            options={[
              { label: 'This Month', value: 'current_month' },
              { label: 'Last 30 Days', value: 'last_30_days' },
              { label: 'Last 90 Days', value: 'last_90_days' },
              { label: 'This Year', value: 'current_year' },
              { label: 'All Time', value: 'total' },
            ]}
            value={currentPeriod}
            onChange={changePeriod}
          />
        </Flex>
      </Flex>
      <ReactEChartsCore
        echarts={echarts}
        option={getOptions}
        style={{ height: '400px' }}
      />
    </>
  );
};

export default InventoryItemChart;

